import * as ft from 'funtypes'

/**
 * Canonical authentication and payment methods, plus the mapping from EIPA's
 * dictionary ids onto them.
 *
 * Unlike `connector_interface`, these dictionary rows carry no machine name -
 * only a Polish `description` - so a name-based mapping is impossible. Their
 * ids are safe to hardcode because they are normative bit flags fixed by the
 * UDT system description (Tabela 1 and Tabela 2), not registry-assigned keys.
 * Unknown ids fold into `other`.
 */

export const AuthMethod = ft.Union(
  ft.Literal('unrestricted'),
  ft.Literal('no-access'),
  ft.Literal('rfid-mifare-classic'),
  ft.Literal('rfid-mifare-desfire'),
  ft.Literal('rfid-calypso'),
  ft.Literal('pinpad'),
  ft.Literal('app'),
  ft.Literal('phone-rfid'),
  ft.Literal('iso15118-plc'),
  ft.Literal('iso15118-wireless'),
  ft.Literal('phone-voice'),
  ft.Literal('phone-sms'),
  ft.Literal('prepaid-card'),
  ft.Literal('other'),
)
export type AuthMethod = ft.Static<typeof AuthMethod>

export const PaymentMethod = ft.Union(
  ft.Literal('unspecified'),
  ft.Literal('free'),
  ft.Literal('operator-contract'),
  ft.Literal('payment-card'),
  ft.Literal('cash'),
  ft.Literal('prepaid-card'),
  ft.Literal('fleet-card'),
  ft.Literal('bank-transfer'),
  ft.Literal('online-payment'),
  ft.Literal('other'),
)
export type PaymentMethod = ft.Static<typeof PaymentMethod>

/** Tabela 1: `dictionary.station_authentication_method` id -> canonical slug. */
export const EIPA_AUTH_METHOD_BY_ID: Readonly<Record<number, AuthMethod>> = {
  0: 'unrestricted',
  1: 'no-access',
  2: 'rfid-mifare-classic',
  4: 'rfid-mifare-desfire',
  8: 'rfid-calypso',
  16: 'pinpad',
  32: 'app',
  64: 'phone-rfid',
  128: 'iso15118-plc',
  256: 'iso15118-wireless',
  512: 'phone-voice',
  1024: 'phone-sms',
  8192: 'prepaid-card',
}

/** Tabela 2: `dictionary.station_payment_method` id -> canonical slug. */
export const EIPA_PAYMENT_METHOD_BY_ID: Readonly<Record<number, PaymentMethod>> = {
  0: 'unspecified',
  1: 'free',
  2: 'operator-contract',
  4: 'payment-card',
  8: 'cash',
  16: 'prepaid-card',
  32: 'fleet-card',
  64: 'bank-transfer',
  128: 'online-payment',
}

export const authMethodFromEipaId = (id: number): AuthMethod =>
  EIPA_AUTH_METHOD_BY_ID[id] ?? 'other'

export const paymentMethodFromEipaId = (id: number): PaymentMethod =>
  EIPA_PAYMENT_METHOD_BY_ID[id] ?? 'other'
